<template>
  <div class="pp-page">
    <!-- Header -->
    <div class="pp-header">
      <div class="pp-header-inner">
        <div class="pp-logo">simitrac</div>
        <div class="pp-school" v-if="data">{{ data.institution }}</div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="pp-loading">
      <div class="spinner-border" style="color:#2E86DE;"></div>
      <div style="margin-top:12px;color:#8BA4C4;">Loading student information...</div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="pp-error-wrap">
      <div class="pp-error-icon">🔒</div>
      <h4>{{ error }}</h4>
      <p>This link may have expired or is invalid. Please contact the school for a new link.</p>
    </div>

    <!-- Content -->
    <div v-else-if="data" class="pp-content">

      <!-- Student Card -->
      <div class="pp-card pp-student-card">
        <div class="pp-avatar">{{ initials }}</div>
        <div class="pp-student-info">
          <div class="pp-student-name">{{ data.student.name }}</div>
          <div class="pp-student-meta">{{ data.student.class_name || 'No class' }} · {{ data.student.admission_no }}</div>
        </div>
        <div class="pp-status-badge" :class="isPresent ? 'pp-badge-green' : 'pp-badge-red'">
          {{ isPresent ? '✅ At School' : '🏠 Not at School' }}
        </div>
      </div>

      <!-- Today's Attendance -->
      <div class="pp-card">
        <div class="pp-card-title"><i class="mdi mdi-clock-check me-2"></i>Today's Attendance</div>
        <div v-if="data.attendance && data.attendance.length">
          <div class="pp-timeline">
            <div class="pp-timeline-item" v-for="(tap, i) in data.attendance" :key="i">
              <div class="pp-timeline-dot" :style="{background: tapColor(tap.tap_point_type)}"></div>
              <div class="pp-timeline-line" v-if="i < data.attendance.length - 1"></div>
              <div class="pp-timeline-body">
                <div class="pp-timeline-time">{{ formatTime(tap.tapped_at) }}</div>
                <div class="pp-timeline-label">{{ tapLabel(tap.tap_point_type) }}</div>
                <a v-if="tap.gps_lat && tap.gps_lng" :href="'https://maps.google.com/?q=' + tap.gps_lat + ',' + tap.gps_lng" target="_blank" class="pp-gps-link">
                  <i class="mdi mdi-map-marker"></i> View Location
                </a>
              </div>
            </div>
          </div>
          <!-- Map -->
          <div id="parentMap" class="pp-map" v-if="hasGps"></div>
        </div>
        <div v-else class="pp-empty">No attendance recorded today.</div>
      </div>

      <!-- Wallet -->
      <div class="pp-card">
        <div class="pp-card-title"><i class="mdi mdi-wallet me-2"></i>Wallet</div>
        <div class="pp-wallet-balance">
          <span class="pp-wallet-label">Balance</span>
          <span class="pp-wallet-amount">KSh {{ formatNum(data.wallet.balance) }}</span>
        </div>
        <div v-if="data.wallet.transactions && data.wallet.transactions.length" class="pp-tx-list">
          <div class="pp-card-subtitle">Recent Transactions</div>
          <div class="pp-tx" v-for="tx in data.wallet.transactions" :key="tx.id">
            <div class="pp-tx-left">
              <div class="pp-tx-desc">{{ tx.description || tx.type }}</div>
              <div class="pp-tx-date">{{ formatDate(tx.created_at) }}</div>
            </div>
            <div class="pp-tx-amount" :class="tx.amount > 0 ? 'pp-amount-green' : 'pp-amount-red'">
              {{ tx.amount > 0 ? '+' : '' }}KSh {{ formatNum(Math.abs(tx.amount)) }}
            </div>
          </div>
        </div>
        <div v-else class="pp-empty">No recent transactions.</div>
      </div>

      <!-- Fees -->
      <div class="pp-card">
        <div class="pp-card-title"><i class="mdi mdi-school me-2"></i>School Fees</div>
        <div class="pp-fee-summary">
          <div class="pp-fee-item">
            <div class="pp-fee-label">Total Fees</div>
            <div class="pp-fee-val">KSh {{ formatNum(data.fees.total) }}</div>
          </div>
          <div class="pp-fee-item">
            <div class="pp-fee-label">Paid</div>
            <div class="pp-fee-val" style="color:#10B981;">KSh {{ formatNum(data.fees.paid) }}</div>
          </div>
          <div class="pp-fee-item">
            <div class="pp-fee-label">Balance</div>
            <div class="pp-fee-val" style="color:#EF4444;font-weight:800;">KSh {{ formatNum(data.fees.balance) }}</div>
          </div>
        </div>
        <div v-if="data.fees.payments && data.fees.payments.length" class="pp-tx-list">
          <div class="pp-card-subtitle">Payment History</div>
          <div class="pp-tx" v-for="p in data.fees.payments" :key="p.id">
            <div class="pp-tx-left">
              <div class="pp-tx-desc">{{ p.description || 'Fee Payment' }}</div>
              <div class="pp-tx-date">{{ formatDate(p.created_at) }}</div>
            </div>
            <div class="pp-tx-amount pp-amount-green">KSh {{ formatNum(p.amount) }}</div>
          </div>
        </div>
        <div v-else class="pp-empty">No fee payments recorded.</div>
      </div>

      <!-- Footer -->
      <div class="pp-footer">
        <div>© 2026 Simi Trac — Save Smart, Spend Smart</div>
        <a href="https://simitrac.com">simitrac.com</a>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { executeJson, simitrack } from "@/api";
export default {
  name: "ParentPortal",
  data() {
    return { loading: true, error: '', data: null };
  },
  computed: {
    initials() {
      if (!this.data || !this.data.student) return '?';
      var parts = this.data.student.name.trim().split(' ');
      return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : this.data.student.name.slice(0, 2).toUpperCase();
    },
    isPresent() {
      if (!this.data || !this.data.attendance || !this.data.attendance.length) return false;
      var last = this.data.attendance[this.data.attendance.length - 1];
      return last.tap_point_type !== 'gate_exit';
    },
    hasGps() {
      if (!this.data || !this.data.attendance) return false;
      return this.data.attendance.some(t => t.gps_lat && t.gps_lng);
    }
  },
  mounted() { this.loadPortal(); this.loadLeaflet(); },
  methods: {
    loadLeaflet() {
      if (!document.getElementById('leaflet-css-pp')) {
        var link = document.createElement('link'); link.id = 'leaflet-css-pp'; link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link);
      }
      if (!window.L) {
        var script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; document.head.appendChild(script);
      }
    },
    async loadPortal() {
      var token = this.$route.params.token;
      if (!token) { this.error = 'Invalid link'; this.loading = false; return; }
      try {
        var r = await executeJson({ function: 'parentPortal', token: token }, simitrack);
        if (r.data.success) {
          this.data = r.data.data;
          this.$nextTick(() => { setTimeout(() => this.initMap(), 500); });
        } else {
          this.error = r.data.message || 'Link expired or invalid';
        }
      } catch (e) { this.error = 'Connection error'; }
      this.loading = false;
    },
    initMap() {
      if (!window.L || !this.hasGps) return;
      var mapEl = document.getElementById('parentMap');
      if (!mapEl) return;
      var map = L.map('parentMap').setView([0, 0], 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap', maxZoom: 19
      }).addTo(map);
      var bounds = [];
      this.data.attendance.forEach(t => {
        if (t.gps_lat && t.gps_lng) {
          var lat = parseFloat(t.gps_lat), lng = parseFloat(t.gps_lng);
          var color = this.tapColor(t.tap_point_type);
          var icon = L.divIcon({
            className: 'pp-map-marker',
            html: '<div style="width:12px;height:12px;border-radius:50%;background:' + color + ';border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);"></div>',
            iconSize: [12, 12], iconAnchor: [6, 6]
          });
          L.marker([lat, lng], { icon: icon }).addTo(map).bindPopup(this.tapLabel(t.tap_point_type) + '<br>' + this.formatTime(t.tapped_at));
          bounds.push([lat, lng]);
        }
      });
      if (bounds.length) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 17 });
    },
    tapColor(type) {
      var c = { gate_entry: '#10B981', gate_exit: '#EF4444', classroom: '#2E86DE', bus_boarding: '#F59E0B', bus_dropoff: '#FB923C', event: '#A855F7' };
      return c[type] || '#8BA4C4';
    },
    tapLabel(type) {
      var l = { gate_entry: 'Arrived at School', gate_exit: 'Left School', classroom: 'Classroom Check-in', bus_boarding: 'Boarded Bus', bus_dropoff: 'Dropped Off', event: 'Event Check-in' };
      return l[type] || type;
    },
    formatTime(dt) {
      if (!dt) return '-';
      return new Date(dt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
    formatDate(dt) {
      if (!dt) return '-';
      return new Date(dt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    formatNum(n) { return Number(n || 0).toLocaleString(); }
  }
};
</script>

<style scoped>
*{box-sizing:border-box;}
.pp-page{min-height:100vh;background:#F0F2F5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}
.pp-header{background:linear-gradient(135deg,#0D2B5E,#1A4B8C);padding:0;}
.pp-header-inner{max-width:480px;margin:0 auto;padding:20px 16px;text-align:center;}
.pp-logo{color:#fff;font-size:22px;font-weight:800;letter-spacing:-0.5px;}
.pp-school{color:rgba(255,255,255,.7);font-size:13px;margin-top:2px;}
.pp-loading{text-align:center;padding:60px 20px;}
.pp-error-wrap{text-align:center;padding:60px 20px;max-width:400px;margin:0 auto;}
.pp-error-icon{font-size:60px;margin-bottom:16px;}
.pp-error-wrap h4{color:#333;font-size:18px;}
.pp-error-wrap p{color:#666;font-size:14px;}
.pp-content{max-width:480px;margin:0 auto;padding:16px;}

/* Student Card */
.pp-card{background:#fff;border-radius:14px;padding:18px;margin-bottom:14px;box-shadow:0 1px 3px rgba(0,0,0,.06);}
.pp-student-card{display:flex;align-items:center;gap:14px;flex-wrap:wrap;}
.pp-avatar{width:50px;height:50px;border-radius:50%;background:linear-gradient(135deg,#2E86DE,#1A4B8C);color:#fff;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:800;flex-shrink:0;}
.pp-student-info{flex:1;min-width:0;}
.pp-student-name{font-size:17px;font-weight:800;color:#1a1a1a;}
.pp-student-meta{font-size:12px;color:#888;margin-top:2px;}
.pp-status-badge{padding:4px 12px;border-radius:50px;font-size:12px;font-weight:700;white-space:nowrap;}
.pp-badge-green{background:#ECFDF5;color:#059669;}
.pp-badge-red{background:#FEF2F2;color:#DC2626;}

.pp-card-title{font-size:15px;font-weight:700;color:#1a1a1a;margin-bottom:14px;display:flex;align-items:center;}
.pp-card-title i{color:#2E86DE;font-size:20px;}
.pp-card-subtitle{font-size:12px;font-weight:600;color:#888;text-transform:uppercase;letter-spacing:.5px;margin:14px 0 8px;}

/* Timeline */
.pp-timeline{margin-bottom:12px;}
.pp-timeline-item{display:flex;gap:12px;position:relative;padding-bottom:14px;}
.pp-timeline-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;margin-top:5px;z-index:1;}
.pp-timeline-line{position:absolute;left:4px;top:15px;bottom:0;width:2px;background:#E5E7EB;}
.pp-timeline-body{flex:1;}
.pp-timeline-time{font-size:14px;font-weight:700;color:#1a1a1a;}
.pp-timeline-label{font-size:12px;color:#666;margin-top:1px;}
.pp-gps-link{font-size:11px;color:#2E86DE;text-decoration:none;display:inline-flex;align-items:center;gap:2px;margin-top:2px;}
.pp-gps-link:hover{text-decoration:underline;}

/* Map */
.pp-map{width:100%;height:180px;border-radius:10px;overflow:hidden;margin-top:8px;}

/* Wallet */
.pp-wallet-balance{display:flex;justify-content:space-between;align-items:center;background:#F8FAFC;border-radius:10px;padding:14px;margin-bottom:8px;}
.pp-wallet-label{color:#888;font-size:13px;}
.pp-wallet-amount{font-size:24px;font-weight:800;color:#1a1a1a;}

/* Transactions */
.pp-tx-list{border-top:1px solid #F0F0F0;padding-top:4px;}
.pp-tx{display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #F8F8F8;}
.pp-tx:last-child{border-bottom:none;}
.pp-tx-left{flex:1;min-width:0;}
.pp-tx-desc{font-size:13px;color:#333;font-weight:600;}
.pp-tx-date{font-size:11px;color:#999;margin-top:1px;}
.pp-tx-amount{font-size:14px;font-weight:700;white-space:nowrap;margin-left:12px;}
.pp-amount-green{color:#059669;}
.pp-amount-red{color:#DC2626;}

/* Fees */
.pp-fee-summary{display:flex;gap:8px;margin-bottom:8px;}
.pp-fee-item{flex:1;background:#F8FAFC;border-radius:10px;padding:12px;text-align:center;}
.pp-fee-label{font-size:11px;color:#888;margin-bottom:4px;}
.pp-fee-val{font-size:16px;font-weight:700;color:#1a1a1a;}

.pp-empty{text-align:center;color:#aaa;font-size:13px;padding:16px 0;}

.pp-footer{text-align:center;padding:24px 16px;color:#aaa;font-size:12px;}
.pp-footer a{color:#2E86DE;text-decoration:none;}
</style>